/**
 * Persistência Barbearia — localStorage + IndexedDB.
 * Gate: nunca autosave estado vazio antes de terminar o load.
 */

import {
  estadoInicialBarbearia,
  normalizarEstadoBarbearia,
} from './barbeariaLogica';
import type { BarbeariaEstado } from './barbeariaTipos';

export const LS_BARBEARIA = 'ade-barbearia-estado-v1';
export const BARBEARIA_IDB_NAME = 'ade-barbearia-db';
export const BARBEARIA_IDB_VERSION = 1;
export const BARBEARIA_IDB_STORE = 'estado';
const IDB_KEY = 'atual';

function abrirIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponível'));
      return;
    }
    const req = indexedDB.open(BARBEARIA_IDB_NAME, BARBEARIA_IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(BARBEARIA_IDB_STORE)) {
        db.createObjectStore(BARBEARIA_IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(): Promise<BarbeariaEstado | null> {
  try {
    const db = await abrirIDB();
    const data = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(BARBEARIA_IDB_STORE, 'readonly');
      const r = tx.objectStore(BARBEARIA_IDB_STORE).get(IDB_KEY);
      r.onsuccess = () => resolve(r.result ?? null);
      r.onerror = () => reject(r.error);
    });
    db.close();
    if (!data || typeof data !== 'object') return null;
    return normalizarEstadoBarbearia(data);
  } catch {
    return null;
  }
}

async function idbPut(estado: BarbeariaEstado): Promise<void> {
  try {
    const db = await abrirIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(BARBEARIA_IDB_STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(BARBEARIA_IDB_STORE).put(estado, IDB_KEY);
    });
    db.close();
  } catch {
    /* ignore */
  }
}

function lerLs(): BarbeariaEstado | null {
  try {
    const raw = localStorage.getItem(LS_BARBEARIA);
    if (!raw) return null;
    return normalizarEstadoBarbearia(JSON.parse(raw));
  } catch {
    return null;
  }
}

function gravarLs(estado: BarbeariaEstado): void {
  try {
    localStorage.setItem(LS_BARBEARIA, JSON.stringify(estado));
  } catch {
    /* ignore */
  }
}

function scoreEstado(e: BarbeariaEstado | null): number {
  if (!e) return -1;
  return (
    e.agendamentos.length * 10 +
    e.clientes.length * 5 +
    e.despesas.length * 3 +
    e.estoque.length +
    (Date.parse(e.atualizadoEm) || 0) / 1e13
  );
}

/** Une LS + IDB preferindo a fonte com mais dados / mais recente. */
export async function carregarEstadoBarbearia(): Promise<BarbeariaEstado> {
  const ls = lerLs();
  const idb = await idbGet();
  if (!ls && !idb) return estadoInicialBarbearia();
  if (!ls) return idb!;
  if (!idb) return ls;
  return scoreEstado(idb) >= scoreEstado(ls) ? idb : ls;
}

export async function persistirEstadoBarbearia(estado: BarbeariaEstado): Promise<void> {
  // Não altera atualizadoEm aqui — quem muda o estado já define; evita “parecer mais novo” e sobrescrever a nuvem à toa
  gravarLs(estado);
  await idbPut(estado);
}
